import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { AdminContext } from '../../contexts/admin.context';
import { AccountInfoSchema } from '../../schema/profile.schema';
import { getAccountDetails, updateAccountDetails, getProjects, updateProject } from '../../service';

import Button from '../common/Button';
import AdminInput from '../common/AdminInput';
import { ThumbsUpIcon } from '../icons';
import { getTranslation } from '../../utils/translation';
import { getLocalizedName, getSortedList } from '../../utils';

const AccountInfo = () => {
  const {
    setPopupInfo,
    themeId,
    user,
    setUser,

    teamType,
    themeConfig: { buttonRadius } = {},
    portalConfig: { regions, establishments, academies, registrationForm, languages } = {}
  } = useContext(AdminContext);

  const [accountDetails, setAccountDetails] = useState({});

  const { t, i18n } = useTranslation();

  const fieldEnabled = (fieldName) => {
    return registrationForm.find((field) => field.id === fieldName)?.enabled;
  };

  const errorBaseKey = 'schema.AccountInfoSchema';

  const getErrorMessage = (error) => {
    const errorPath = `${errorBaseKey}.${error}`;
    const translatedError = t(errorPath);

    return translatedError === errorPath ? getTranslation(t, errorPath) : translatedError;
  };

  const isEmptyValue = (value) => value === undefined || value === null || `${value}`.trim() === '';

  const validateRequiredFields = (values) => {
    const errors = {};

    if (isEmptyValue(values.companyName)) {
      errors.companyName = 'school';
    }

    if (isEmptyValue(values.region)) {
      errors.region = 'region';
    }

    if (fieldEnabled('postalCode') && isEmptyValue(values.postalCode)) {
      errors.postalCode = 'postalCode';
    }

    if (fieldEnabled('city') && isEmptyValue(values.city)) {
      errors.city = 'city';
    }

    return errors;
  };

  const handleSubmit = async (formValues) => {
    let updatedAccountDetails = { ...accountDetails };

    try {
      updatedAccountDetails.companyName = formValues.companyName.trim();
      updatedAccountDetails.addresses[0].street = formValues.region;
      updatedAccountDetails.addresses[0].postcode = formValues.postalCode;
      updatedAccountDetails.addresses[0].city = formValues.city;
      updatedAccountDetails.addresses[0].postbox = formValues.postbox;
      updatedAccountDetails = {
        ...updatedAccountDetails,
        specs: {
          ...updatedAccountDetails?.specs,
          academy: formValues.academy,
          establishment: formValues.establishment,
          schoolOrganization: formValues.schoolOrganization
        }
      };

      await updateAccountDetails(updatedAccountDetails);
      const copyUser = { ...user };
      copyUser.accounts[0].companyName = formValues.companyName.trim();
      setUser({ ...copyUser, address: updatedAccountDetails.addresses });

      // Get all projects and update teacherRegion
      const { data: projects } = await getProjects();
      if (projects?.length) {
        for (const project of projects) {
          const projectValues = {
            ...project,
            specs: {
              ...project.specs,
              teacherRegion: formValues.region
            }
          };
          await updateProject(projectValues);
        }
      }

      // show succcess popup
      setPopupInfo({
        isVisible: true,
        icon: ThumbsUpIcon,
        heading: getTranslation(t, 'accountInfo.ChangesUpdated')
      });
    } catch (e) {
      toast.error(getTranslation(t, 'accountInfo.errors.failedUpload'));
      console.error('Failed to update account details:', e);
    }
  };

  const formik = useFormik({
    initialValues: {
      accountId: '',
      companyName: '',
      establishment: '',
      region: '',
      academy: '',
      schoolOrganization: '',
      postalCode: '',
      city: ''
    },
    validationSchema: AccountInfoSchema,
    validate: validateRequiredFields,
    onSubmit: handleSubmit
  });

  // Load account info
  useEffect(() => {
    const fetchAccountDetails = async () => {
      if (!themeId) return;

      try {
        const response = await getAccountDetails();
        // set form values
        // set in state - to be used for API
        const { data: details } = response;
        setAccountDetails(details);

        // form values
        const formValues = {
          accountId: details.id,
          companyName: details.companyName,
          establishment: details?.specs?.establishment,
          region: details.addresses[0].street,
          academy: details?.specs?.academy,
          schoolOrganization: details?.specs?.schoolOrganization,
          postalCode: details.addresses[0]?.postcode,
          city: details.addresses[0]?.city
        };
        formik.setValues(formValues, false);
      } catch (e) {
        toast.error(getTranslation(t, 'accountInfo.errors.failedUpload'));
        console.error('Failed to load account details:', e);
      }
    };

    fetchAccountDetails();
  }, [themeId, teamType]);

  return (
    <>
      <div className="mt-12 w-full">
        <h1
          className="text-2xl font-bold text-left text-cblack-primary mb-2.5"
          dangerouslySetInnerHTML={{
            __html: getTranslation(t, `accountInfo.profile.accountInfo.title`)
          }}
        />

        <div className="mt-5 max-w-xl">
          <form onSubmit={formik.handleSubmit} className="mb-8">
            <AdminInput
              label={getTranslation(
                t,
                `accountInfo.profile.accountInfo.formFields.accountId.label`
              )}
              placeholder={getTranslation(
                t,
                `accountInfo.profile.accountInfo.formFields.accountId.placeholder`
              )}
              type="text"
              name="accountId"
              formik={formik}
              disabled
            />

            {fieldEnabled('type') && (
              <div className="mb-8">
                <label className="block mb-1 text-base font-bold text-cblack-primary">
                  {getTranslation(
                    t,
                    `accountInfo.profile.accountInfo.formFields.establishment.label`
                  )}
                </label>
                <select
                  className={`px-4 py-3.5 w-full text-gray-primary bg-gray-100 rounded-md`}
                  style={{ borderRadius: buttonRadius }}
                  id="establishment"
                  name="establishment"
                  value={formik.values.establishment}
                  onBlur={formik.handleBlur}
                  onChange={(event) => {
                    formik.setFieldValue('establishment', event.target.value);
                  }}>
                  {establishments.map((establishment) => (
                    <option
                      key={establishment._id}
                      value={establishment._id}
                      data-id={establishment._id}>
                      {establishment['name_' + i18n.language]}
                    </option>
                  ))}
                </select>
                {formik.touched.establishment && formik.errors.establishment && (
                  <p className="ml-1 text-sm text-[#ef5252] mt-1">
                    {getErrorMessage(formik.errors.establishment)}
                  </p>
                )}
              </div>
            )}
            <AdminInput
              label={getTranslation(
                t,
                `accountInfo.profile.accountInfo.formFields.companyName.label`
              )}
              placeholder={getTranslation(
                t,
                `accountInfo.profile.accountInfo.formFields.companyName.placeholder`
              )}
              type="text"
              name="companyName"
              errorBaseKey={errorBaseKey}
              formik={formik}
            />

            <div className="mb-8">
              <label className="block mb-1 text-base font-bold text-cblack-primary">
                {getTranslation(t, `accountInfo.profile.accountInfo.formFields.region.label`)}
              </label>
              <select
                className={`px-4 py-3.5 w-full text-gray-primary bg-gray-100 rounded-md`}
                style={{ borderRadius: buttonRadius }}
                id="region"
                name="region"
                value={formik.values.region}
                onBlur={formik.handleBlur}
                onChange={(event) => {
                  const selectedOption = event.target.options[event.target.selectedIndex];
                  const selectedRegionId = selectedOption.getAttribute('data-id');
                  formik.setFieldValue('region', event.target.value);
                  formik.setFieldValue('postbox', selectedRegionId);
                }}>
                {getSortedList(regions, i18n.language, languages).map((region) => (
                  <option key={region._id} value={region._id} data-id={region._id}>
                    {getLocalizedName(region, i18n.language, languages)}
                  </option>
                ))}
              </select>
              {formik.touched.region && formik.errors.region && (
                <p className="ml-1 text-sm text-[#ef5252] mt-1">
                  {getErrorMessage(formik.errors.region)}
                </p>
              )}
            </div>
            {fieldEnabled('academy') && (
              <div className="mb-8">
                <label className="block mb-1 text-base font-bold text-cblack-primary">
                  {getTranslation(t, `accountInfo.profile.accountInfo.formFields.academy.label`)}
                </label>
                <select
                  className={`px-4 py-3.5 w-full text-gray-primary bg-gray-100 rounded-md`}
                  style={{ borderRadius: buttonRadius }}
                  id="academy"
                  name="academy"
                  value={formik.values.academy}
                  onBlur={formik.handleBlur}
                  onChange={(event) => {
                    formik.setFieldValue('academy', event.target.value);
                  }}>
                  {academies.map((academy) => (
                    <option key={academy._id} value={academy._id} data-id={academy._id}>
                      {academy['name_' + i18n.language]}
                    </option>
                  ))}
                </select>
                {formik.touched.academy && formik.errors.academy && (
                  <p className="ml-1 text-sm text-[#ef5252] mt-1">
                    {getErrorMessage(formik.errors.academy)}
                  </p>
                )}
              </div>
            )}

            {fieldEnabled('postalCode') && (
              <AdminInput
                label={getTranslation(
                  t,
                  `accountInfo.profile.accountInfo.formFields.postalCode.label`
                )}
                placeholder={getTranslation(
                  t,
                  `accountInfo.profile.accountInfo.formFields.postalCode.placeholder`
                )}
                type="text"
                name="postalCode"
                errorBaseKey={errorBaseKey}
                formik={formik}
              />
            )}
            {fieldEnabled('city') && (
              <AdminInput
                label={getTranslation(t, `accountInfo.profile.accountInfo.formFields.city.label`)}
                placeholder={getTranslation(
                  t,
                  `accountInfo.profile.accountInfo.formFields.city.placeholder`
                )}
                type="text"
                name="city"
                errorBaseKey={errorBaseKey}
                formik={formik}
              />
            )}

            <Button buttonType="primary" type="submit" loading={formik.isSubmitting}>
              {getTranslation(t, `accountInfo.profile.accountInfo.actionButtons.save`)}
            </Button>
          </form>
        </div>
      </div>
    </>
  );
};

export default AccountInfo;
